import React, { useState } from 'react'
import useSWRMutation from 'swr/mutation'
import { mutate } from 'swr'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import type { FieldValues } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { PlusIcon } from '@radix-ui/react-icons'
import { postFetcher } from 'api/swr'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Form } from '@/components/ui/form'
import Loader from '@/components/ui/Loader'
import { useToast } from '@/components/ui/use-toast'
import { MyFormInput, MyFromTextarea } from 'components/ui/MyFormField'
import {
  formatTagNameForLevel,
  TAG_LEVEL_NAME_HINT,
  tagNameLevelError,
} from 'commons/tagName'

const infoFormSchema = z
  .object({
    level: z.preprocess((val) => Number(val), z.number().min(2).max(3)),
    name: z.string().min(2).max(30),
    note: z.string().max(640).min(2),
  })
  .superRefine((data, ctx) => {
    const formatted = formatTagNameForLevel(
      data.name,
      data.level === 3 ? 3 : 2,
    )
    const invalid = tagNameLevelError(formatted, data.level === 3 ? 3 : 2)
    if (invalid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['name'],
        message: invalid,
      })
    }
  })

export type CreatedTag = {
  id?: string
  name: string
  level: 2 | 3
}

type CreateTagDialogProps = {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  trigger?: React.ReactNode
  onCreated?: (tag: CreatedTag) => void
}

export const CreateTagDialog = ({
  open,
  onOpenChange,
  trigger,
  onCreated,
}: CreateTagDialogProps) => {
  const { toast } = useToast()
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const isControlled = open != null
  const isOpen = isControlled ? open : uncontrolledOpen
  const setIsOpen = onOpenChange || setUncontrolledOpen
  const { trigger: createTag, isMutating, error } = useSWRMutation(
    '/taglibs',
    postFetcher,
  )
  const form = useForm<FieldValues>({
    resolver: zodResolver(infoFormSchema),
    defaultValues: {
      level: 2,
      name: '',
      note: '',
    },
  })

  const onSubmit = (data: FieldValues) => {
    if (isMutating) return
    const level = Number(data.level) === 3 ? 3 : 2
    const name = formatTagNameForLevel(String(data.name || ''), level)
    createTag(
      { ...data, level, name },
      {
        onSuccess: (result: { id?: string }) => {
          mutate((key) => Array.isArray(key) && key[0] === '/taglibs')
          setIsOpen(false)
          form.reset({ level: 2, name: '', note: '' })
          toast({ title: 'Tag created' })
          onCreated?.({ id: result?.id, name, level })
        },
        onError: (err: { message?: string }) => {
          toast({
            variant: 'destructive',
            title: 'Create tag failed',
            description: err?.message || 'Unknown error',
          })
        },
      },
    )
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(next) => {
        setIsOpen(next)
        if (!next) form.reset({ level: 2, name: '', note: '' })
      }}
    >
      {trigger}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Tag</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <MyFormInput
              type="number"
              name="level"
              form={form}
              label="level"
              placeholder="tag level"
            />
            <MyFormInput
              name="name"
              form={form}
              label="Name"
              placeholder="tag name"
            />
            <p className="text-xs text-muted-foreground">
              {TAG_LEVEL_NAME_HINT}
            </p>
            <MyFromTextarea
              name="note"
              form={form}
              label="Description"
              placeholder="tag description"
            />
            {isMutating ? (
              <Loader>Submit...</Loader>
            ) : (
              <Button type="submit">Submit</Button>
            )}
            {error && <div className="text-red-500">{error.message}</div>}
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

export const CreateTagButton = ({
  onCreated,
}: {
  onCreated?: (tag: CreatedTag) => void
}) => (
  <CreateTagDialog
    trigger={
      <DialogTrigger asChild>
        <Button variant="default">
          <PlusIcon className="mr-2 h-4 w-4" />
          <span className="sr-only sm:not-sr-only sm:whitespace-nowrap">
            Create Tag
          </span>
        </Button>
      </DialogTrigger>
    }
    onCreated={onCreated}
  />
)
